/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useOrganizationContext } from "../../contexts/OrganizationContext";
import { GetPractitionerRole } from "../../services/fhir";
import { useValueSet } from "../../hooks/useValueSet";
import { useFormatter } from "../../utils/useFormatter";
import { ISelectOption } from "./interfaces";
import { GetValueSet } from "@/services/terminology";
import { HTTPResponseStatus } from "@/utils/enum";
import { ISpecialty } from "@/pages/professionalregister/Types";
import { UFValueSet as UFValueSetList } from "../../utils/useValueSet";

// Steps Components
import { ModelSelectionStep } from "./steps/ModelSelectionStep";
import { StateSelectionStep } from "./steps/StateSelectionStep";
import { SpecialtySelectionStep } from "./steps/SpecialtySelectionStep";
import { FiltersStep } from "./steps/FiltersStep";

const defaultValues: { [key: string]: ISelectOption | undefined } = {
  healthcareService: undefined,
  organization: undefined,
  practitioner: undefined,
  state: undefined,
  serviceModel: undefined,
};

/**
 * @interface HiddenFilters
 * @description Controla quais filtros devem ser ocultados
 */
export interface HiddenFilters {
  date?: boolean; // Oculta o filtro de data
  practitioner?: boolean; // Oculta o filtro de profissional
  clinic?: boolean; // Oculta o filtro de clínica/organização
  /**
   * Oculta/“trava” o filtro de modelo de atendimento (tele/presencial).
   * Usado quando não existe escolha real (ex.: só há agendas virtuais).
   */
  serviceModel?: boolean;
  /**
   * Oculta/“trava” o filtro de estado (UF).
   * Usado quando não existe escolha real (ex.: só há 1 UF disponível no presencial).
   */
  state?: boolean;
}

interface IProps {
  onSubmit: any;
  control: any;
  watch: any;
  resetField: any;
  handleSubmit: Function;
  availableSpecialties?: any[]; // Especialidades disponíveis nos schedules
  setValue: any;
  isSearching?: boolean;
  onModelCleared?: () => void; // Callback quando o modelo de atendimento é limpo
  hiddenFilters?: HiddenFilters; // Prop para ocultar filtros específicos
  availableStates?: string[]; // Lista de estados (UF) disponíveis para filtrar quando presencial
}

export function SearchFields({
  onSubmit,
  control,
  watch,
  resetField,
  handleSubmit,
  availableSpecialties = [],
  setValue,
  isSearching,
  onModelCleared,
  hiddenFilters,
  availableStates,
}: IProps) {
  const { state } = useLocation();

  const [loading, setLoading] = useState(false);
  const [healthcareServiceList, setHealthcareServiceList] = useState<any[]>([]);

  const { formatNameDisplay } = useFormatter;

  const isSelectOption = (el: any): el is ISelectOption => el.label && el.value;

  const { id } = useParams();

  const [practitionerOptions, setPractionerOptions] = useState<any>();

  const {
    options: healthcareServiceOptions,
    loading: healthcareServiceLoading,
  } = useValueSet({
    valueSet: "CBO",
    customUrl: "https://fluxmed.cloud/fhir/r4/ValueSet/CBO-S",
  });

  useEffect(() => {
    async function asyncCall() {
      setLoading(true);
      const EspecialidadeMedicaValueSet = await GetValueSet({
        valueset: "EspecialidadeMedica",
      }).then((response) => {
        if (response?.status === HTTPResponseStatus.Success) {
          return response.data.expansion.contains;
        }
        return [];
      });
      setHealthcareServiceList(
        EspecialidadeMedicaValueSet.map((item: ISpecialty) => ({
          value: item.code,
          label: item.display,
        }))
      );

      setLoading(false);
    }
    asyncCall();
  }, []);

  const auxHealthcareService =
    healthcareServiceOptions
      ?.map((item) => ({
        value: item.code,
        label: item.display,
        display: item.display,
      }))
      ?.sort((a, b) => (a.label.includes("Médico") ? -1 : 1)) || [];

  const allHealthcareServices = [
    ...healthcareServiceList,
    ...auxHealthcareService,
  ];

  const healthcareService = allHealthcareServices;

  if (state) {
    for (const [key, value] of Object.entries(state)) {
      if (defaultValues.hasOwnProperty(key) && isSelectOption(value)) {
        defaultValues[key] = value;
      }
    }
  }

  const watchHealthcareService = watch("healthcareService");
  const watchOrganization = watch("organization");
  const watchServiceModel = watch("serviceModel");
  const watchState = watch("state");
  const watchDate = watch("date");
  const watchPractitioner = watch("practitioner");

  // Timer para debounce da busca automática
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Busca Automática quando os filtros mudam
  // Busca após modelo/estado ser selecionado, e refina quando especialidade é selecionada
  useEffect(() => {
    // Busca se tiver modelo selecionado (especialidade é opcional)
    if (watchServiceModel) {
      // Se for presencial, só busca se tiver estado também
      const isPresencial = watchServiceModel?.value === "physicalLocation";
      const canSearch = isPresencial ? watchState : true;

      if (canSearch) {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
          handleSubmit(onSubmit)();
        }, 500); // 500ms de atraso para evitar chamadas excessivas
      }
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [
    // Dependências que disparam a busca
    watchServiceModel,
    watchState,
    // watchHealthcareService removido para evitar busca automática quando especialidade muda
    // A busca de especialidade será manual via auxOnChange no FiltersStep
    watchOrganization,
    watchDate,
    watchPractitioner,
  ]);

  // Para todos os modelos não-presenciais, garante que o estado (UF) seja limpo
  useEffect(() => {
    if (VIRTUAL_LIKE_MODELS.includes(watchServiceModel?.value)) {
      setValue("state", null);
    }
  }, [watchServiceModel?.value]);

  useEffect(() => {
    async function fetchPractitionerRole() {
      const practitionerRoleData = watchOrganization
        ? await GetPractitionerRole({
            organization: watchOrganization?.value,
          })
        : await GetPractitionerRole({
            includePractitioner: true,
          });

      const practitionerRole = practitionerRoleData?.entry?.filter(
        (e: any) =>
          e?.resource?.resourceType === "PractitionerRole" &&
          !e?.resource?.id?.includes("05490544000100")
      );

      const practitioner = practitionerRoleData?.entry?.filter(
        (e: any) => e?.resource?.resourceType === "Practitioner"
      );

      const filterPractitioners = practitioner?.filter((e: any) => {
        const auxRole = practitionerRole?.some((role: any) => {
          return role?.resource?.id?.includes(e?.resource?.id);
        });

        return auxRole;
      });

      const pracOptions = filterPractitioners?.map((el: any) => {
        const auxIdentifier = el?.resource?.id?.split("-")?.[1];
        const auxRoles = practitionerRole?.filter((role: any) => {
          const auxRole = role?.resource?.id?.includes(auxIdentifier);
          const isFromSelectedOrganization = watchOrganization
            ? role?.resource?.organization?.reference?.includes(
                watchOrganization?.value
              )
            : true;

          return auxRole && isFromSelectedOrganization;
        });

        const auxSpecialties = auxRoles
          ?.map((role: any) => {
            return role?.resource?.specialty;
          })
          ?.filter((e: any) => e?.length);

        const practitionerSpecialties = auxSpecialties?.flatMap(
          (item: any) => item
        );

        return {
          label: formatNameDisplay(el?.resource?.name?.[0]?.text),
          value: el?.resource?.id,
          specialties: practitionerSpecialties,
        };
      });

      const auxOptions = pracOptions?.filter((e: any) => {
        const hasSelectSpecialty = watchHealthcareService
          ? e?.specialties?.some(
              (sp: any) =>
                sp?.coding?.[0]?.code === watchHealthcareService?.value
            )
          : true;

        return hasSelectSpecialty;
      });

      const sortedOptions = auxOptions?.sort((a: any, b: any) => {
        const nameA = a?.label?.toLowerCase() || "";
        const nameB = b?.label?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      });

      setPractionerOptions(sortedOptions);
    }
    fetchPractitionerRole();
  }, [watchHealthcareService, formatNameDisplay, watchOrganization]);

  useEffect(() => {
    if (!practitionerOptions?.length) resetField("practitioner");
  }, [practitionerOptions]);

  // Modelos que não precisam de estado (UF) para busca — disparam imediatamente
  const VIRTUAL_LIKE_MODELS = ["virtualService", "chatService", "domicilioService"];

  const MODEL_LABELS: Record<string, string> = {
    virtualService:   "Teleatendimento",
    physicalLocation: "Presencial",
    chatService:      "Chat",
    domicilioService: "Atendimento a Domicílio",
  };

  const handleModelSelect = (value: string) => {
    setValue("serviceModel", { label: MODEL_LABELS[value] ?? value, value });
    setValue("state", null);
    // Sempre limpa a seleção de especialidade quando o modelo muda
    resetField("healthcareService");

    // Modelos não-presenciais disparam busca imediatamente (sem precisar de estado)
    if (VIRTUAL_LIKE_MODELS.includes(value)) {
      setTimeout(() => handleSubmit(onSubmit)(), 300);
    }
    // Se for presencial, aguarda seleção do estado
  };

  const handleStateSelect = (uf: string) => {
    const option = UFValueSetList.find((item) => item.value === uf);
    setValue("state", option);
    // Sempre limpa a seleção de especialidade quando o estado muda
    resetField("healthcareService");
    // Dispara busca automática após selecionar estado (presencial)
    setTimeout(() => handleSubmit(onSubmit)(), 300);
  };

  const handleSpecialtySelect = (specialty: any) => {
    setValue("healthcareService", specialty);
    // Dispara busca automática após selecionar especialidade
    setTimeout(() => handleSubmit(onSubmit)(), 300);
  };

  const renderFormFields = () => {
    const isPresencial = watchServiceModel?.value === "physicalLocation";

    // Etapa 1: Seleção de Modelo
    if (!watchServiceModel) {
      return <ModelSelectionStep onSelectModel={handleModelSelect} />;
    }

    // Etapa 2: Seleção de Estado (apenas Presencial)
    if (isPresencial && !watchState) {
      return (
        <StateSelectionStep
          onSelectState={handleStateSelect}
          onBack={() => {
            setValue("serviceModel", null);
            resetField("healthcareService");
            // Chama callback para limpar profissionais quando volta para seleção de modelo
            if (onModelCleared) {
              onModelCleared();
            }
          }}
          availableStates={availableStates}
        />
      );
    }

    // Etapa 3: Filtros (especialidade é opcional e pode ser selecionada aqui)
    // Os cards já são exibidos após modelo/estado, e a especialidade filtra quando selecionada
    return (
      <FiltersStep
        control={control}
        watchServiceModel={watchServiceModel}
        watchState={watchState}
        watchHealthcareService={watchHealthcareService}
        onClearModel={() => {
          setValue("serviceModel", null);
          setValue("healthcareService", null);
          // Chama callback para limpar profissionais quando modelo é removido
          if (onModelCleared) {
            onModelCleared();
          }
        }}
        onClearState={() => {
          setValue("state", null);
          // Sempre limpa a especialidade quando limpa o estado
          resetField("healthcareService");
        }}
        onClearSpecialty={() => setValue("healthcareService", null)}
        onSubmit={handleSubmit(onSubmit)}
        isSearching={isSearching}
        healthcareServiceOptions={healthcareService || []}
        organizationOptions={[]}
        practitionerOptions={practitionerOptions || []}
        healthcareServiceLoading={healthcareServiceLoading || loading}
        hiddenFilters={hiddenFilters}
      />
    );
  };

  return <div style={{ width: "100%" }}>{renderFormFields()}</div>;
}
